import { Flex } from '../UI-CHAKRA/Flex';

import { Certificate, CERTIFICATES, CERTIFIER_STATUS, certificates } from '../../containers/certificate/certificates';
import { GridItem } from '../UI-CHAKRA/GridItem';
import CertificateItem from '../../containers/certificate/CertificateItem';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { Button } from '../UI-CHAKRA/Button';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Text } from '../UI-CHAKRA/Text';
import { useMemo, useState } from 'react';
import { ReactComponent as AlertIcon } from '../../assets/images/icons/mevo/alert.svg';

interface CertificateListProps {
  onSelect(value: CERTIFICATES): void;
  onClose(): void;
  selectedCertificateType: CERTIFICATES | null;
  lastCertificateHasError?: boolean;
}

export default function CertificateList(props: CertificateListProps) {
  const { setHideManualSigningMethod, hideManualSigningMethod } = usePrescriptionStore();

  const allCertificates = usePrescriptionStore((store) => store.userCertificates());

  const isDigitalCertificateRequired = usePrescriptionStore((store) => store.digitalCertificateIsRequired());

  const userCertificates: Certificate[] = useMemo(() => {
    if (
      props.selectedCertificateType === CERTIFICATES.MANUAL &&
      !allCertificates.some((c) => c.value === CERTIFICATES.MANUAL)
    ) {
      allCertificates.push(certificates.find((c) => c.value === CERTIFICATES.MANUAL)!);
      return allCertificates;
    }

    if (props.lastCertificateHasError) {
      allCertificates.forEach((cert) => {
        if (cert.value === props.selectedCertificateType) cert.certifierStatus = CERTIFIER_STATUS.RECENT_ERROR;
      });

      if (allCertificates.some((c) => c.value === CERTIFICATES.MANUAL)) return allCertificates;

      allCertificates.push(certificates.find((c) => c.value === CERTIFICATES.MANUAL)!);

      return allCertificates;
    } else return allCertificates;
  }, [allCertificates, props.lastCertificateHasError, props.selectedCertificateType]);

  const enableMandatoryDigitalCertificate = useMemo(() => {
    if (
      !userCertificates ||
      userCertificates.length === 0 ||
      !userCertificates.some((cert) => cert.value !== CERTIFICATES.MANUAL)
    )
      return false;
    return isDigitalCertificateRequired;
  }, [isDigitalCertificateRequired, userCertificates]);

  const [selectedCertificate, setSelectedCertificate] = useState(
    props.lastCertificateHasError ? null : props.selectedCertificateType
  );

  const isMobile = useIsMobile();

  const certificateName = userCertificates.find((c) => c.value === props.selectedCertificateType)?.name;

  return (
    <Flex flexDirection={'column'} gap={'20px'}>
      {props.lastCertificateHasError ? (
        <Flex alignItems={'center'} gap={'6px'}>
          <AlertIcon width="25px" height="25px" />
          <Text fontWeight={700} fontSize={'20px'} color={'primary.main'}>
            Não foi possível assinar com o/a {certificateName}
          </Text>
        </Flex>
      ) : (
        <Text fontWeight={700} fontSize={'20px'} color={'primary.main'}>
          Selecione seu certificado para assinar digitalmente
        </Text>
      )}
      {props.lastCertificateHasError && (
        <Text fontWeight={400} fontSize={'14px'} color={'primary.main'}>
          O certificado {certificateName} está apresentando instabilidade. Você pode tentar novamente, selecionar{' '}
          <b>outro certificado</b> ou usar a opção <b>“Assinar e Carimbar”</b>.
        </Text>
      )}
      <Flex gap={'10px'} flexWrap={'wrap'} justifyContent={{ base: 'center', md: 'flex-start' }}>
        {userCertificates.map((certificate) => {
          const isCertificateA1A3 = certificate.value === 'LOCAL';

          return (
            <GridItem
              colSpan={{ base: 6, md: 2 }}
              display={{
                base: isCertificateA1A3 ? 'none' : 'block',
                md: 'block',
              }}
              w={{ base: '100px', md: '120px' }}
              key={certificate.value}
            >
              <CertificateItem
                certificate={certificate}
                active={certificate.value === selectedCertificate}
                onSelect={() => setSelectedCertificate(certificate.value)}
              />
            </GridItem>
          );
        })}

        {hideManualSigningMethod && !enableMandatoryDigitalCertificate && (
          <Button
            onClick={() => setHideManualSigningMethod(false)}
            h={{ base: '25px', md: '120px' }}
            minW={'25px'}
            w={{ base: '120px', md: '25px' }}
            variant={'ghost'}
            borderRadius={'20px'}
            bg={'gray.lightest'}
            data-testid={'show-manual-signing-method'}
          >
            {isMobile ? <ChevronDownIcon w={'20px'} h={'20px'} /> : <ChevronRightIcon w={'20px'} h={'20px'} />}
          </Button>
        )}
      </Flex>
      <Flex flexDirection={'row'} gap={'16px'} width={'100%'} justifyContent={'center'}>
        <Button width={'100%'} backgroundColor={'#FFF'} color={'primary.main'} onClick={props.onClose}>
          Cancelar
        </Button>
        <Button
          width={'100%'}
          backgroundColor={'primary.main'}
          color={'#FFF'}
          borderRadius={'20px'}
          onClick={() => props.onSelect(selectedCertificate as CERTIFICATES)}
        >
          Concluir
        </Button>
      </Flex>
    </Flex>
  );
}
